import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface ValidationChecks {
    fullFaceVisible: boolean;
    eyesVisible: boolean;
    smileWithOpenMouth: boolean;
    appropriateDistance: boolean;
}

interface ValidationFeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
    errors: string[];
    checks: ValidationChecks | null;
}

const ValidationFeedbackModal: React.FC<ValidationFeedbackModalProps> = ({
    isOpen,
    onClose,
    errors,
    checks
}) => {
    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                />

                {/* Modal Container */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ type: "spring", duration: 0.5 }}
                    className="relative w-full max-w-lg overflow-hidden border shadow-2xl bg-zinc-900 border-zinc-700/50 rounded-2xl ring-1 ring-white/5"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="relative p-6 pb-4 border-b border-zinc-800">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-500/10 ring-1 ring-red-500/20">
                                <svg
                                    className="w-6 h-6 text-red-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white">Photo Quality Check</h3>
                                <p className="text-sm text-zinc-400">
                                    We need a better photo to generate your smile design.
                                </p>
                            </div>
                        </div>

                        {/* Close button */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2 transition-colors rounded-full hover:bg-zinc-800 text-zinc-500 hover:text-white"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Body */}
                    <div className="p-6 space-y-6">

                        {/* Detailed Checks Grid */}
                        {checks && (
                            <div className="bg-zinc-950/50 rounded-xl border border-zinc-800 p-4">
                                <h4 className="text-sm font-medium text-zinc-400 mb-3 uppercase tracking-wider">Quality Criteria</h4>
                                <div className="grid grid-cols-1 gap-3">
                                    <CheckItem
                                        label="Full Face Visible"
                                        passed={checks.fullFaceVisible}
                                        desc="Forehead to chin, full width"
                                    />
                                    <CheckItem
                                        label="Eyes Visible"
                                        passed={checks.eyesVisible}
                                        desc="Open eyes, no sunglasses"
                                    />
                                    <CheckItem
                                        label="Open Smile"
                                        passed={checks.smileWithOpenMouth}
                                        desc="Teeth must be clearly visible"
                                    />
                                    <CheckItem
                                        label="Camera Distance"
                                        passed={checks.appropriateDistance}
                                        desc="Not too far or too close"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Error Messages */}
                        <div className="space-y-3">
                            {errors.map((error, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="flex items-start gap-3 p-3 text-sm text-red-200 rounded-lg bg-red-500/5 border border-red-500/10"
                                >
                                    <svg className="w-5 h-5 text-red-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>{error}</span>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 pt-0">
                        <button
                            onClick={onClose}
                            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl transition-all shadow-lg shadow-indigo-500/20 active:scale-[0.98]"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            Upload New Photo
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

// Check Item Component
const CheckItem: React.FC<{ label: string; passed: boolean; desc: string }> = ({ label, passed, desc }) => (
    <div className="flex items-center justify-between group">
        <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center w-6 h-6 rounded-full ${passed ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}`}>
                {passed ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                )}
            </div>
            <div>
                <span className={`text-sm font-medium ${passed ? 'text-zinc-200' : 'text-zinc-300'}`}>{label}</span>
                <p className="text-xs text-zinc-500">{desc}</p>
            </div>
        </div>
        {/* Status Badge */}
        <div className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${passed ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
            {passed ? 'PASS' : 'FAIL'}
        </div>
    </div>
);

export default ValidationFeedbackModal;
